import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const parseAuthToken = () => {
    try {
        const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
        return authUser ? String(authUser) : "";
    } catch {
        return "";
    }
};

/** Same API basic auth as `createAxiosInstance` in `config/api.config.js`. */
const buildAtsBasicAuthorizationHeader = () => {
    const username = "FUNNTPYKLWJN";
    const password = "d7#Vt9X$kLp!r5Wc?yZ3uB*m";
    if (typeof btoa !== "function") {
        return "";
    }
    return `Basic ${btoa(`${username}:${password}`)}`;
};

const buildOrderPlaceManualFetchHeaders = () => {
    const token = parseAuthToken();
    const authorization = buildAtsBasicAuthorizationHeader();
    return {
        Accept: "application/json",
        ...(authorization ? { Authorization: authorization } : {}),
        ...(token ? { "X-Access-Token": token } : {}),
    };
};

export const readFileAsArrayBuffer = async (file) => {
    if (!(file instanceof Blob) || file.size <= 0) {
        throw new Error("Invalid file");
    }
    return file.arrayBuffer();
};

export const fetchPdfArrayBufferFromUrl = async (url = "", options = {}) => {
    const normalizedUrl = String(url || "").trim();
    if (!normalizedUrl) {
        throw new Error("PDF URL is missing");
    }

    const skipAuth = Boolean(options.skipAuth);
    const token = skipAuth ? "" : parseAuthToken();
    const headers = token ? { "X-Access-Token": token } : {};

    const response = await fetch(normalizedUrl, {
        method: "GET",
        headers,
        credentials: skipAuth ? "omit" : "include",
        cache: "no-store",
    });

    if (!response.ok) {
        throw new Error(`Unable to load PDF (${response.status})`);
    }

    return response.arrayBuffer();
};

const buildOrderPlaceManualApiUrl = (orderPlaceFileUrl = "") => {
    const normalized = String(orderPlaceFileUrl || "").trim().replace(/\/$/, "");
    if (!normalized) return "";
    if (/\/manual$/i.test(normalized)) return normalized;
    return `${normalized}/manual`;
};

const extractPdfUrlFromManualResponse = (payload) => {
    if (!payload || typeof payload !== "object") return "";
    const direct = payload.url ?? payload.fileUrl ?? payload.downloadUrl;
    if (typeof direct === "string" && direct.trim()) {
        return direct.trim();
    }
    const nested = payload.data?.url ?? payload.data?.fileUrl ?? payload.data?.downloadUrl;
    if (typeof nested === "string" && nested.trim()) {
        return nested.trim();
    }
    return "";
};

/**
 * Loads application PDFs from `fadvOrderPlaceFile`: GET `{url}/manual` → `{ url: "<s3 signed>" }`,
 * then fetches PDF bytes from that S3 URL (CORS-friendly for the editor).
 */
export const fetchPdfArrayBufferFromOrderPlaceFileUrl = async (orderPlaceFileUrl = "") => {
    const apiUrl = String(orderPlaceFileUrl || "").trim();
    if (!apiUrl) {
        throw new Error("PDF URL is missing");
    }

    if (/^https:\/\/[^/]+\.s3[.-][^/]+\.amazonaws\.com\//i.test(apiUrl)) {
        return fetchPdfArrayBufferFromUrl(apiUrl, { skipAuth: true });
    }

    const manualBase = buildOrderPlaceManualApiUrl(apiUrl);
    const manualSeparator = manualBase.includes("?") ? "&" : "?";
    const manualUrl = `${manualBase}${manualSeparator}_=${Date.now()}`;
    const manualResponse = await fetch(manualUrl, {
        method: "GET",
        headers: buildOrderPlaceManualFetchHeaders(),
        credentials: "include",
        cache: "no-store",
    });

    if (manualResponse.ok) {
        let payload = null;
        try {
            payload = await manualResponse.json();
        } catch {
            throw new Error("Invalid response from application PDF manual endpoint");
        }
        const signedPdfUrl = extractPdfUrlFromManualResponse(payload);
        if (!signedPdfUrl) {
            throw new Error("Application PDF URL missing in manual response");
        }
        return fetchPdfArrayBufferFromUrl(signedPdfUrl, { skipAuth: true });
    }

    throw new Error(`Unable to load PDF (${manualResponse.status})`);
};

const embedImageBytes = async (pdfDoc, bytes, mimeType = "") => {
    const normalizedMime = String(mimeType || "").toLowerCase();
    if (normalizedMime.includes("png")) {
        return pdfDoc.embedPng(bytes);
    }
    return pdfDoc.embedJpg(bytes);
};

const readFormFieldState = (field) => {
    const typeName = field?.constructor?.name || "";
    const name = field.getName();

    if (typeName === "PDFTextField") {
        return { name, type: "text", value: String(field.getText() || "") };
    }
    if (typeName === "PDFCheckBox") {
        return { name, type: "checkbox", value: Boolean(field.isChecked()) };
    }
    if (typeName === "PDFDropdown") {
        const selected = field.getSelected();
        return {
            name,
            type: "dropdown",
            value: Array.isArray(selected) ? String(selected[0] || "") : String(selected || ""),
            options: field.getOptions() || [],
        };
    }
    if (typeName === "PDFRadioGroup") {
        return {
            name,
            type: "radio",
            value: String(field.getSelected() || ""),
            options: field.getOptions() || [],
        };
    }
    return { name, type: "unknown", value: "" };
};

const applyFormFieldValue = (form, name, fieldState = {}) => {
    if (!name) return;
    try {
        const field = form.getField(name);
        const typeName = field?.constructor?.name || "";
        const { value } = fieldState;

        if (typeName === "PDFTextField") {
            field.setText(String(value ?? ""));
            return;
        }
        if (typeName === "PDFCheckBox") {
            if (value) field.check();
            else field.uncheck();
            return;
        }
        if (typeName === "PDFDropdown") {
            field.select(String(value ?? ""));
            return;
        }
        if (typeName === "PDFRadioGroup") {
            field.select(String(value ?? ""));
        }
    } catch {
        // ignore missing or unsupported fields
    }
};

/** Load page sizes and fillable form fields from an existing PDF. */
export const loadPdfEditorContext = async (sourceBytes) => {
    const pdfDoc = await PDFDocument.load(sourceBytes, { ignoreEncryption: true });
    const pageCount = pdfDoc.getPageCount();
    const pageSizes = [];

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        const { width, height } = pdfDoc.getPage(pageIndex).getSize();
        pageSizes.push({ width, height });
    }

    const formFields = [];
    try {
        const form = pdfDoc.getForm();
        formFields.push(...form.getFields().map(readFormFieldState));
    } catch {
        // PDF has no AcroForm
    }

    return { pageCount, pageSizes, formFields };
};

/**
 * @param {ArrayBuffer|Uint8Array} sourceBytes
 * @param {{ formFieldValues?: Record<string, { type?: string, value?: unknown }>, textOverlays?: Array<{ text: string, page?: number, x?: number, y?: number, fontSize?: number }>, imageOverlays?: Array<{ bytes: ArrayBuffer|Uint8Array, mimeType?: string, page?: number, x?: number, y?: number, width?: number, height?: number }>, flattenForm?: boolean }} edits
 */
export const buildEditedPdfBytes = async (sourceBytes, edits = {}) => {
    const pdfDoc = await PDFDocument.load(sourceBytes, { ignoreEncryption: true });
    const pageCount = pdfDoc.getPageCount();

    const formFieldValues = edits?.formFieldValues || {};
    const formFieldNames = Object.keys(formFieldValues);
    if (formFieldNames.length) {
        try {
            const form = pdfDoc.getForm();
            formFieldNames.forEach((fieldName) => {
                applyFormFieldValue(form, fieldName, formFieldValues[fieldName]);
            });
            if (edits?.flattenForm) {
                form.flatten();
            }
        } catch {
            // ignore form errors
        }
    }

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const textColor = rgb(0, 0, 0);

    const textOverlays = Array.isArray(edits?.textOverlays) ? edits.textOverlays : [];
    for (const overlay of textOverlays) {
        const text = String(overlay?.text || "").trim();
        if (!text) continue;

        const pageNumber = Math.min(pageCount, Math.max(1, Number(overlay?.page) || 1));
        const page = pdfDoc.getPage(pageNumber - 1);
        const fontSize = Math.max(6, Number(overlay?.fontSize) || 12);
        const x = Number(overlay?.x);
        const y = Number(overlay?.y);

        page.drawText(text, {
            x: Number.isFinite(x) ? x : 50,
            y: Number.isFinite(y) ? y : 50,
            size: fontSize,
            font,
            color: textColor,
        });
    }

    const imageOverlays = Array.isArray(edits?.imageOverlays) ? edits.imageOverlays : [];
    for (const overlay of imageOverlays) {
        const bytes = overlay?.bytes;
        if (!bytes || (bytes.byteLength !== undefined && bytes.byteLength <= 0)) continue;

        const pageNumber = Math.min(pageCount, Math.max(1, Number(overlay?.page) || 1));
        const page = pdfDoc.getPage(pageNumber - 1);
        const image = await embedImageBytes(pdfDoc, bytes, overlay?.mimeType);
        const width = Math.max(1, Number(overlay?.width) || 120);
        const height = Math.max(1, Number(overlay?.height) || 80);
        const x = Number(overlay?.x);
        const y = Number(overlay?.y);

        page.drawImage(image, {
            x: Number.isFinite(x) ? x : 50,
            y: Number.isFinite(y) ? y : 50,
            width,
            height,
        });
    }

    return pdfDoc.save();
};

const GENERIC_PDF_BASENAMES = new Set([
    "document",
    "untitled",
    "pdflib_creation_example",
    "pdf-lib_creation_example",
]);

/** True when the name is empty or a pdf-lib / browser placeholder, not a real upload name. */
export const isGenericPdfFileName = (fileName = "") => {
    const trimmed = String(fileName || "").trim();
    if (!trimmed) return true;
    const base = trimmed.replace(/\.pdf$/i, "").toLowerCase();
    return GENERIC_PDF_BASENAMES.has(base);
};

/** Last path segment of a URL, decoded (e.g. S3 key ending in `.pdf`). */
export const extractFileNameFromUrl = (url = "") => {
    const normalized = String(url || "").trim();
    if (!normalized) return "";

    try {
        const pathname = new URL(normalized, window.location.origin).pathname;
        const segment = decodeURIComponent(pathname.split("/").filter(Boolean).pop() || "");
        if (!segment) return "";
        return segment.toLowerCase().endsWith(".pdf") ? segment : `${segment}.pdf`;
    } catch {
        const withoutQuery = normalized.split("?")[0].split("#")[0];
        const segment = decodeURIComponent(withoutQuery.split("/").filter(Boolean).pop() || "");
        if (!segment) return "";
        return segment.toLowerCase().endsWith(".pdf") ? segment : `${segment}.pdf`;
    }
};

export const resolvePdfOutputFileName = (preferred = "", fallback = "document.pdf") => {
    const preferredTrimmed = String(preferred || "").trim();
    if (preferredTrimmed && !isGenericPdfFileName(preferredTrimmed)) {
        return preferredTrimmed.toLowerCase().endsWith(".pdf")
            ? preferredTrimmed
            : `${preferredTrimmed}.pdf`;
    }

    const fallbackTrimmed = String(fallback || "").trim();
    if (fallbackTrimmed && !isGenericPdfFileName(fallbackTrimmed)) {
        return fallbackTrimmed.toLowerCase().endsWith(".pdf")
            ? fallbackTrimmed
            : `${fallbackTrimmed}.pdf`;
    }

    return "document.pdf";
};

export const pdfBytesToFile = (bytes, fileName = "document.pdf", fallbackName = "document.pdf") => {
    const normalizedName = resolvePdfOutputFileName(fileName, fallbackName);
    const normalizedBytes =
        bytes instanceof Uint8Array ? new Uint8Array(bytes) : new Uint8Array(bytes);
    return new File([normalizedBytes], normalizedName, {
        type: "application/pdf",
        lastModified: Date.now(),
    });
};

export const saveSinglePagePdfBytes = async (sourceBytes, pageNumber) => {
    const src = await PDFDocument.load(sourceBytes, { ignoreEncryption: true });
    const pageIndex = Math.max(0, Math.min(src.getPageCount() - 1, pageNumber - 1));
    const out = await PDFDocument.create();
    const [copied] = await out.copyPages(src, [pageIndex]);
    out.addPage(copied);
    return out.save();
};

export const getPdfPageCount = async (sourceBytes) => {
    const pdfDoc = await PDFDocument.load(sourceBytes, { ignoreEncryption: true });
    return pdfDoc.getPageCount();
};

export const createPdfPreviewBlobUrl = (bytes) => {
    const blob = new Blob([bytes], { type: "application/pdf" });
    return URL.createObjectURL(blob);
};
